import { REWRITE_TIERS } from '@lyrmo/shared';
import type { Messages } from '../../i18n/en';
import type { I18n } from '../../i18n/i18n-context';
import type { RewriteState } from './useRewrite';

type SuccessState = Extract<RewriteState, { status: 'success' }>;

/** The original and every version that came back, each under its heading. */
export function allVersionsText({ text, rewrites }: SuccessState, t: Messages): string {
  const sections: [string, string][] = [[t.results.original, text]];
  for (const tier of REWRITE_TIERS) {
    const version = rewrites[tier];
    if (version) sections.push([t.tiers[tier].label, version]);
  }
  return sections
    .map(([heading, body]) => `${heading}\n${'-'.repeat(heading.length)}\n${body.trimEnd()}\n`)
    .join('\n');
}

/** What screen readers are told when a rewrite finishes or fails. */
export function rewriteAnnouncement(
  state: RewriteState,
  { t, format, describeError }: I18n,
): string {
  const total = REWRITE_TIERS.length;
  switch (state.status) {
    case 'idle':
      return '';
    // Said, as the sign beside the heading has no words (RewriteSign).
    case 'loading':
      return t.results.rewriting;
    case 'error':
      if (state.error.code === 'RATE_LIMITED') {
        return state.error.retryAfter === undefined
          ? t.results.announce.rateLimited
          : t.results.announce.rateLimitedFor(format.duration(state.error.retryAfter));
      }
      if (state.error.code === 'CREDIT_LIMIT_REACHED') {
        return t.results.announce.creditLimit(describeError(state.error));
      }
      return t.results.announce.failed;
    case 'success': {
      const ready = REWRITE_TIERS.filter((tier) => state.rewrites[tier]).length;
      return ready === total
        ? t.results.announce.ready(ready)
        : t.results.announce.partlyReady(ready, total - ready);
    }
  }
}
